import React, { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  BrowserRouter as Router,
} from "react-router-dom";
import logo from "./logo.svg";
import "./App.css";
import { IConfig } from "./types/IConfig";
import Spinner from "./components/Spinner/Spinner";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";

// Eager: small, on nearly every route (guards / layouts / auth).
import { PrivateRoute } from "./pages/PrivateRoute/PrivateRoute";
import { AuthProvider } from "./utility/useAuth";
import { DefaultLayout } from "./layouts/Default";
import GuestLayout from "./layouts/Guest";
// Eager, and a SIBLING of <Suspense> below — see the comment at its mount point.
import SupportAssistantHost from "./components/SupportAssistant/SupportAssistantHost";
import ErrorDialogHost from "./components/Modals/ErrorModal/ErrorDialogHost";
import { useStoreState } from "./store";
import { userIsManager, userIsCorp } from "./utility/userRole";
import { isAnalyticsEnabled } from "./utility/analyticsAccess";

// Route screens are code-split so each ships as its own chunk — the initial
// bundle no longer pulls in every page's Syncfusion imports. The
// webpackChunkName comments name the output files (e.g. page-chart.<hash>.chunk.js)
// so the bundle-size report is readable; see scripts/check-bundle-size.js.
const Home = lazy(() => import(/* webpackChunkName: "page-home" */ "./pages/Home"));
const Login = lazy(() => import(/* webpackChunkName: "page-login" */ "./pages/Login"));
const Logout = lazy(() => import(/* webpackChunkName: "page-logout" */ "./pages/Logout"));
const JobPage = lazy(() => import(/* webpackChunkName: "page-job" */ "./pages/JobPage"));
const RoutePage = lazy(() => import(/* webpackChunkName: "page-routing" */ "./pages/RoutingPage"));
const BomPage = lazy(() => import(/* webpackChunkName: "page-bom" */ "./pages/BomPage"));
const JobImagesPage = lazy(() => import(/* webpackChunkName: "page-job-images" */ "./pages/ImagesPage/JobImagesPage"));
const SimilarJobsImagesPage = lazy(() => import(/* webpackChunkName: "page-similar-jobs-images" */ "./pages/SimilarJobsImages"));
const ImagesPage = lazy(() => import(/* webpackChunkName: "page-images" */ "./pages/ImagesPage/ImagesPage"));
const DocumentsPage = lazy(() => import(/* webpackChunkName: "page-documents" */ "./pages/ImagesPage/DocumentsPage"));
const SystemPage = lazy(() => import(/* webpackChunkName: "page-system" */ "./pages/SystemPage/SystemPage"));
const OperationsPage = lazy(() => import(/* webpackChunkName: "page-operations" */ "./pages/OperationsPage/OperationsPage"));
const UsersPage = lazy(() => import(/* webpackChunkName: "page-users" */ "./pages/UsersPage/UsersPage"));
const JobDocumentsPage = lazy(() => import(/* webpackChunkName: "page-job-documents" */ "./pages/JobDocuments/JobDocumentsPage"));
const DiagnosticPage = lazy(() => import(/* webpackChunkName: "page-diagnostic" */ "./pages/DiagnosticPage/DiagnosticPage"));
const HierarchicalPage = lazy(() => import(/* webpackChunkName: "page-hierarchical" */ "./pages/HierarchicalPage/HierarchicalPage"));
const ReportPage = lazy(() => import(/* webpackChunkName: "page-report" */ "./pages/ReportPage/ReportPage"));
const TACPage = lazy(() => import(/* webpackChunkName: "page-terms-and-conditions" */ "./pages/TACPage/TACPage"));
const OrgChangePage = lazy(() => import(/* webpackChunkName: "page-org-change" */ "./pages/OrgChangePage/OrgChangePage"));
const OptOutPage = lazy(() => import(/* webpackChunkName: "page-opt-out" */ "./pages/OptOutPage/OptOutPage"));


/** Paths of discontinued screens, redirected to the home board. See the route list below. */
const DISCONTINUED_PATHS: readonly string[] = [
  "/chart", "/allocations", "/tool", "/CompareBOM", "/CapacitySettings", "/TestResults",
  "/Calibrations", "/Toolset", "/Certifications",
  "third-party-visual-inspection/:sequence?", "cnc-route-card-visual-inspection/:sequence?",
  "factory-acceptance-inspection/:sequence?", "packaging-inspection/:sequence?",
  "barcode/:sequenceNumber",
];

// Merge our Window interface into the global scope
declare global {
  interface Window {
    config: IConfig;
  }
}

const RoutesWrap: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <App />
      </Router>
    </ErrorBoundary>
  );
};

// --- CORP / CORP2-only wrapper ---
const CorpRoute: React.FC<React.PropsWithChildren> = ({ children }) => {
  const user = useStoreState((s: any) => s.user);

  if (!userIsCorp(user)) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// --- Manager-only wrapper using complexity bit (bit 0) ---
const ManagerRoute: React.FC<React.PropsWithChildren> = ({ children }) => {
  const user = useStoreState((s: any) => s.user);

  if (!userIsManager(user)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

// --- Analytics feature-flag wrapper (Key Vault secret AnalyticsEnabled = "Y") ---
// Guards direct navigation / bookmarks to /Analytics when the feature is off; the
// header entry point (CSMShopfloorHeader) is hidden by the same flag.
const AnalyticsRoute: React.FC<React.PropsWithChildren> = ({ children }) => {
  if (!isAnalyticsEnabled()) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <div className="App">
      {/* <header className="App-header">
        <img src={logo} className="App-logo" alt="logo" />
        <p>
          Edit <code>src/App.tsx</code> and save to reload.
        </p>
        <a
          className="App-link"
          href="https://reactjs.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn React
        </a>
      </header> */}
      <AuthProvider authConfig={null}>
        <Suspense fallback={<Spinner />}>
        <ReactRoutes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Navigate to="/home" />
              </PrivateRoute>
            }
          />
          <Route
            path="/login"
            element={
              <GuestLayout>
                <Login />
              </GuestLayout>
            }
          />
          <Route
            path="/logout"
            element={
              <GuestLayout>
                <Logout />
              </GuestLayout>
            }
          />
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <Home />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="job/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="termsandconditions"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <TACPage />
                </DefaultLayout>
              </PrivateRoute>
            }
            
          />
          <Route
  path="choose-organization"
  element={
    <PrivateRoute>
      <CorpRoute>
        <DefaultLayout>
          <OrgChangePage />
        </DefaultLayout>
      </CorpRoute>
    </PrivateRoute>
  }
/>


          <Route
            path="consent-details"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <OptOutPage />
                </DefaultLayout>
              </PrivateRoute>
            }
            />
           <Route
            path="log/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <RoutePage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />          
          <Route
            path="bom/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <BomPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/images"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <ImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <DocumentsPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />

          {/* --------- MANAGER-ONLY ROUTES (wrapped) --------- */}
          <Route
            path="/system"
            element={
              <PrivateRoute>
                <ManagerRoute>
                  <DefaultLayout>
                    <SystemPage />
                  </DefaultLayout>
                </ManagerRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <ManagerRoute>
                  <DefaultLayout>
                    <UsersPage />
                  </DefaultLayout>
                </ManagerRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/operations"
            element={
              <PrivateRoute>
                <ManagerRoute>
                  <DefaultLayout>
                    <OperationsPage />
                  </DefaultLayout>
                </ManagerRoute>
              </PrivateRoute>
            }
          />
          {/* --------- END MANAGER-ONLY ROUTES --------- */}

          <Route
            path="/diagnostic"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <DiagnosticPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/hierarchical"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <HierarchicalPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/Analytics"
            element={
              <PrivateRoute>
                <AnalyticsRoute>
                  <DefaultLayout>
                    <ReportPage />
                  </DefaultLayout>
                </AnalyticsRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="job-images/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="similar-jobs-images/"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <SimilarJobsImagesPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="job-documents/:jobNumber"
            element={
              <PrivateRoute>
                <DefaultLayout>
                  <JobDocumentsPage />
                </DefaultLayout>
              </PrivateRoute>
            }
          />
          {/* DISCONTINUED SCREENS (2026-09-28). The barcode page, the four inspection reports and the
              manager planning/quality screens are no longer part of CSM. Their old paths go to the
              home board rather than matching nothing, which would render an empty page. The page
              components are no longer imported; the shop-floor board's barcode SEARCH is a
              different feature and is unaffected. */}
          {DISCONTINUED_PATHS.map((path) => (
            <Route key={path} path={path} element={<Navigate to="/home" replace />} />
          ))}
        </ReactRoutes>
        </Suspense>

        {/* ⚠️ A SIBLING OF <Suspense>, NOT A CHILD OF IT, AND THAT PLACEMENT IS THE WHOLE POINT.
            Every screen above is React.lazy behind that ONE boundary, so the first navigation
            to an unvisited route suspends and REPLACES the entire subtree with the spinner —
            unmounting anything inside it. The support assistant used to be mounted down in
            layouts/Default, so following a link the assistant itself had just offered destroyed
            the panel and the conversation in it. It survived the second visit because lazy()
            caches, which is why the bug presented as "open-from-chat sometimes loses my chat".

            Still inside <AuthProvider> and <Router>: it calls the BFF with the user's token and
            it reads and changes the route. Do not move it, and do not make it lazy. */}
        <SupportAssistantHost />

        {/* ⚠️ A SIBLING OF <Suspense> FOR THE SAME REASON, AND A SIBLING OF THE LINE ABOVE FOR
            A DIFFERENT ONE. Under the boundary, a dialog would be unmounted mid-read by the
            first navigation to an unvisited route. Inside SupportAssistantHost, it would
            inherit that host's suppressions — /login, /logout, /diagnostic/* and "terms not
            accepted yet" — which are screens where HELP is not offered, not screens where
            failures stop happening. A failed sign-in is exactly a failure a user needs to be
            able to read. See components/Modals/ErrorModal/ErrorDialogHost. */}
        <ErrorDialogHost />
      </AuthProvider>
    </div>
  );
}

//export default App;

export default RoutesWrap;